import { Injectable } from '@angular/core';
import {
  HttpErrorResponse,
  HttpEvent,
  HttpHandler,
  HttpInterceptor,
  HttpRequest,
  HttpStatusCode,
} from '@angular/common/http';

import { Observable, catchError, switchMap, throwError } from 'rxjs';

import { TAB_IDENTITY_MISMATCH } from '@shared/signup/constants';
import { LoggerService } from '@tablesready/frontend-logger';
import { AuthService, RefreshOutcome } from './auth.service';
import { TabIdentity, TabIdentityService } from './tab-identity.service';

export const SKIP_AUTH_HTTP_HEADER_NAME = 'SKIP_AUTH_HTTP_HEADER_NAME';

/** The location a request was made for, and the login this tab showed when it was made. */
interface RequestOrigin {
  locationId: string | undefined;
  shownAt: TabIdentity | null;
}

@Injectable({providedIn: 'root'})
export class AuthHttpInterceptor implements HttpInterceptor {
  constructor(
    private readonly auth: AuthService,
    private readonly logger: LoggerService,
    private readonly tabIdentity: TabIdentityService,
  ) { }

  intercept(request: HttpRequest<any>, next: HttpHandler): Observable<HttpEvent<any>> {
    // A locked tab is showing another login's or location's screens; nothing it sends can be trusted.
    if (this.tabIdentity.isLocked()) {
      return throwError(() => AuthHttpInterceptor.staleTab(request));
    }

    const shownAt = this.tabIdentity.snapshot();

    // Requests that carry their own Bearer (token refresh, tokenize/login) bypass the gate and the
    // 401-retry to avoid recursion/deadlock. Strip the marker via clone — HttpHeaders is immutable, so
    // `delete()` returns a new instance that must be reassigned (the old code discarded it).
    if (request.headers.has(SKIP_AUTH_HTTP_HEADER_NAME)) {
      return next.handle(request.clone({ headers: request.headers.delete(SKIP_AUTH_HTTP_HEADER_NAME) }));
    }

    // Capture the active location BEFORE `getToken()` — `getToken()` calls `getActive()`, which drops
    // an expired active token, so the location must be read first for the 401 fallback to refresh it.
    const locationId = this.auth.getActiveLocationId();
    const token = this.auth.getToken();
    const requestOrigin: RequestOrigin = {
      locationId,
      shownAt,
    };

    if (token) {
      // A live access token stays valid across routine rotations (the backend refresh is a
      // stateless re-sign; nothing is invalidated server-side), so don't stall the request behind
      // the refresh gate — the 401→refresh→retry below covers the rare genuine-expiry race.
      return this.handleWithAuth(request, next, requestOrigin, token);
    }

    // No usable access token (expired during a freeze, or rotating right now): wait until any
    // in-flight refresh settles, then take whatever it produced. `awaitRefresh()` emits
    // synchronously when no refresh is in flight.
    return this.auth.awaitRefresh().pipe(
      switchMap(() => this.handleWithoutLiveToken(request, next, requestOrigin)),
    );
  }

  /**
   * Nothing here ever sends `Bearer undefined`: the backend rejects it with 401 ("jwt malformed"),
   * so such a request is wasted at best and, from a poller that outlived its session, a steady
   * stream of 401s at worst.
   */
  private handleWithoutLiveToken(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    requestOrigin: RequestOrigin,
  ): Observable<HttpEvent<unknown>> {
    const { locationId } = requestOrigin;
    const settled = this.auth.getToken();
    if (settled) {
      return this.handleWithAuth(request, next, requestOrigin, settled);
    }

    if (!AuthHttpInterceptor.requiresAuth(request)) {
      // Public route (login, app variables, server time): the backend ignores the header there, so
      // send it unsigned and never make it wait on, or fail with, a session refresh.
      return next.handle(request);
    }

    if (!this.auth.isAuthenticated()) {
      // Logged out: an authenticated route cannot succeed, so fail it without a network call.
      return throwError(() => AuthHttpInterceptor.unauthorized(request));
    }

    // A session whose access token lapsed: refresh first instead of sending a request that can
    // only 401. The retry flag makes a second rejection end the session, as in the 401 fallback.
    return this.auth.refreshActiveToken(locationId).pipe(
      switchMap(result => {
        if (result.outcome === RefreshOutcome.success && result.token) {
          return this.handleWithAuth(request, next, requestOrigin, result.token.token, true);
        }

        if (!this.tabIdentity.isStillShowing(requestOrigin.shownAt)) {
          // This tab logged out, logged in or switched location meanwhile: the request is the old screen's.
          return AuthHttpInterceptor.dropSuperseded(request);
        }

        if (result.outcome === RefreshOutcome.authRejected) {
          this.auth.logoutWithExpiredSession();

          return throwError(() => AuthHttpInterceptor.unauthorized(request));
        }

        // Transient refresh failure (offline, timeout): send unsigned so the backend's answer, or
        // a status-0 network failure, reaches the caller unchanged.
        return next.handle(request);
      }),
    );
  }

  /** Mirrors the backend's AuthMiddleware, which guards every path starting with /v.1. */
  static requiresAuth(request: HttpRequest<unknown>): boolean {
    return new URL(request.url, 'http://localhost').pathname.startsWith('/v.1');
  }

  private static unauthorized(request: HttpRequest<unknown>): HttpErrorResponse {
    return new HttpErrorResponse({
      status: HttpStatusCode.Unauthorized,
      statusText: 'Unauthorized',
      url: request.url,
      error: 'Unauthorized',
    });
  }

  private static staleTab(request: HttpRequest<unknown>): HttpErrorResponse {
    return new HttpErrorResponse({
      status: HttpStatusCode.Conflict,
      statusText: 'Conflict',
      url: request.url,
      error: { code: TAB_IDENTITY_MISMATCH },
    });
  }

  /** Fails a request from a screen this tab itself has left. Silent, and the tab stays unlocked. */
  private static dropSuperseded(request: HttpRequest<unknown>): Observable<never> {
    return throwError(() => AuthHttpInterceptor.staleTab(request));
  }

  private static isTabIdentityMismatch(err: HttpErrorResponse): boolean {
    const body: unknown = err.error;
    const code: unknown = typeof body === 'object' && body !== null ? Reflect.get(body, 'code') : null;

    return err.status === Number(HttpStatusCode.Conflict) && code === TAB_IDENTITY_MISMATCH;
  }

  private handleWithAuth(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    requestOrigin: RequestOrigin,
    authToken: string | undefined,
    isRetry = false,
  ): Observable<HttpEvent<unknown>> {
    // The request is from a screen this tab itself has left (new login or location): drop it, don't lock.
    if (!this.tabIdentity.isStillShowing(requestOrigin.shownAt)) {
      return throwError(() => AuthHttpInterceptor.staleTab(request));
    }

    if (authToken && !this.tabIdentity.verifyToken(authToken, 'request')) {
      return throwError(() => AuthHttpInterceptor.staleTab(request));
    }

    return this.sendWithAuth(request, next, requestOrigin, authToken, isRetry);
  }

  private sendWithAuth(
    request: HttpRequest<unknown>,
    next: HttpHandler,
    requestOrigin: RequestOrigin,
    authToken: string | undefined,
    isRetry: boolean,
  ): Observable<HttpEvent<unknown>> {
    const { locationId } = requestOrigin;
    const authedRequest = request.clone({
      setHeaders: {
        Authorization: `Bearer ${authToken}`,
        ...this.tabIdentity.headers(),
      },
    });

    return next.handle(authedRequest).pipe(
      catchError((err: HttpErrorResponse) => {
        if (AuthHttpInterceptor.isTabIdentityMismatch(err)) {
          this.tabIdentity.lock('server');

          return throwError(() => err);
        }

        const isUnauthorized = err.status === 401;
        if (isUnauthorized && !request.url.includes('/login')) {
          // A 401 for a screen this tab has left says nothing about the current login: never refresh or log out.
          if (!this.tabIdentity.isStillShowing(requestOrigin.shownAt)) {
            return AuthHttpInterceptor.dropSuperseded(request);
          }

          if (isRetry) {
            // The retry carried a freshly refreshed token and still 401'd — the session is truly gone.
            return this.logoutAndRethrow(err);
          }

          // Refresh the captured location's token once, then retry with that exact token so a location
          // switch during refresh cannot change the retry's restaurant context. Only a genuine auth
          // rejection logs out; a transient refresh failure rethrows the original 401 WITHOUT logout
          // so a flaky network can't end the session (retry/health-check recovers it).
          return this.auth.refreshActiveToken(locationId).pipe(
            switchMap((result) => {
              if (result.outcome === RefreshOutcome.success && result.token) {
                return this.handleWithAuth(request, next, requestOrigin, result.token.token, true);
              }

              if (!this.tabIdentity.isStillShowing(requestOrigin.shownAt)) {
                // This tab logged out, logged in or switched location during the refresh: neither log
                // out the new login nor report an error.
                return AuthHttpInterceptor.dropSuperseded(request);
              }

              if (result.outcome === RefreshOutcome.authRejected) {
                return this.logoutAndRethrow(err);
              }

              this.logger.error(err);

              return throwError(() => err);
            }),
          );
        }

        // Every 401 except a rejected login was handled above, and the login form reports that one
        // itself. LoggerService.error() is console.error, which RUM records as an app error.
        if (!isUnauthorized) {
          this.logger.error(err);
        }

        return throwError(() => err);
      }),
    );
  }

  private logoutAndRethrow(err: HttpErrorResponse): Observable<never> {
    // Not logged: an expired session is an expected outcome the user already sees (alert and
    // redirect to login), and LoggerService.error() is console.error, which RUM records as an error.
    this.auth.logoutWithExpiredSession();

    return throwError(() => err);
  }
}
